import { match } from 'ts-pattern';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { keys, move, omit } from 'ramda';
import type { Nullish } from 'shared/web';
import { SMART_SEARCH_KEY } from '../constants/smartSearchKey';
import { makeActionLockKey, type ActionLockReason } from '../types/ActionLockState';
import type { ActivePreset, ChoiceFilterOptions, ColumnsSetting, FeGridSettings, GetDataQueryResponse, Preset, TransformedDefinitionApiResponse, VirtualPreset } from '../types/Api';
import type { EagColumnState } from '../types/EagColumnState';
import type { Filters } from '../types/Filters';
import type { QueryFilterObject } from '../types/QueryFilterObject';
import type { RowData } from '../types/RowData';
import type { ToolpanelType } from '../types/ToolpanelType';
import { getSortModel } from '../utils/getSortModel';

/**
 * Complete reactive state slice for a single EagGrid instance.
 *
 * ## Why a store instead of props?
 *
 * EagGrid's component tree is deep: EagGridTable → GridBody → GridRow →
 * GridCell, and separately EagGridTable → GridHeader → toolbar buttons,
 * plus Sidebar → FiltersPanel / ColumnsPanel / SettingsPanel.
 *
 * Without a store, every piece of shared state (loading flag, selected rows,
 * active panel, drag state, …) would have to be passed as props through every
 * intermediate layer that doesn't need it — classic "prop drilling".  The store
 * solves this:
 *
 * - **Leaf components** (GridCell, HeaderCell, skeleton rows) subscribe
 *   only to the slices they care about via `useEagGridSelector(selectX)`.
 * - **Sibling subtrees** (sidebar panels, header toolbar) share state without any
 *   common ancestor having to pass it down.
 * - **Filter UI**: FilterPanel, FilterCategoryMenu and FilterContentArea all need
 *   access to `filterValues` and `activePanel`.  Passing these through EagGridTable →
 *   Sidebar → FilterPanel → FilterCategoryMenu → … would require 4+ layers
 *   of props that carry no value for the intermediate components.  Instead each
 *   component reads from the store directly.
 *
 * ## Instance isolation
 *
 * `createEagGridStore` is a factory — each `<EagGrid>` mount creates its own
 * independent store instance via `EagGridStoreProvider`.  Multiple grids on the
 * same page cannot bleed state into each other.
 *
 * ## Subscription model
 *
 * The store is powered by Zustand (`zustand/vanilla`).  The factory returns
 * a standard `StoreApi<EagGridState>` extended with named action methods
 * (`store.setPage(3)`) so mutations are explicit and discoverable.
 */
export interface EagGridState {
  // Static config — set once at mount via initStaticConfig, never changes
  gridCode: string;
  presetInstanceId: string | Nullish;
  disableAllActions: boolean;

  // Core grid state
  dataQuery: GetDataQueryResponse | Nullish;
  definition: TransformedDefinitionApiResponse | Nullish;
  apiError: string | Nullish;

  // Preset state
  activePreset: ActivePreset | Nullish;
  customPresets: Preset[] | null;
  virtualPreset: VirtualPreset | Nullish;
  isPresetChanged: boolean;
  activePresetDataQuery: GetDataQueryResponse | Nullish;

  // Column state
  columnStates: EagColumnState[];

  // Server data state
  rows: RowData[];
  previousRows: RowData[];
  totalCount: number;
  isLoading: boolean;
  queryId: string;

  // Filter state
  filterValues: Filters;
  /** Static filters applied by the host via queryModifier — excluded from "is filter active" check. */
  staticFilters: QueryFilterObject;

  // Selection state
  selectedRowIds: Set<string>;

  // UI state
  page: number;
  activePanel: ToolpanelType | null;
  /** True after the first applyPresetColumns call — columns have been hydrated from the preset.
   *  Used to suppress the header row and column-aware skeleton until columns are stable. */
  isColumnsReady: boolean;
  isResizing: boolean;
  resizingColumn: string | Nullish;

  // Drag state
  activeDragId: string | Nullish;
  overColumnId: string | Nullish;
  dropSide: 'left' | 'right' | Nullish;

  /** Cache of loaded choice filter options, keyed by column key.
   *  Survives panel close/reopen — populated by useLazyChoiceFilterOptions,
   *  shared across filter panel components. */
  choiceFilterOptionsCache: Map<string, ChoiceFilterOptions>;

  // Action lock state — per-row, per-action loading/disabled tracking
  actionLocks: Map<string, number>;

  // Bulk action loading — true while a bulk action Promise is pending
  isBulkActionLoading: boolean;
}

/**
 * Public API surface of the store returned by `createEagGridStore`.
 *
 * Extends Zustand's `StoreApi<EagGridState>` with named action methods.
 * Components obtain the store via `useEagGridStore()` (which reads from
 * `EagGridStoreContext`) and interact with it through the named action methods
 * rather than calling `setState` directly.  This keeps mutations explicit and
 * makes it obvious which component owns which state transition.
 *
 * Selectors (the `selectX` functions at the bottom of this file) project a slice
 * of `EagGridState` and are used with `useEagGridSelector(selectX)` to subscribe
 * components to only the state they need — avoiding unnecessary re-renders.
 */
export interface EagGridStore extends StoreApi<EagGridState> {
  // Column actions
  setColumnStates: (columnStates: EagColumnState[]) => void;
  setColumnWidth: (key: string, width: number) => void;
  setColumnHidden: (key: string, hidden: boolean) => void;
  setColumnPinned: (key: string, pinned: 'LEFT' | 'RIGHT' | null) => void;
  reorderColumnsByKey: (fromKey: string, toKey: string) => void;
  setColumnSort: (key: string, sort: 'ASC' | 'DESC' | null) => void;
  applyPresetColumns: (columns: ColumnsSetting[]) => void;
  applyPresetColumnsAndSort: (columns: ColumnsSetting[]) => void;

  // Server data actions
  setRows: (rows: RowData[]) => void;
  setTotalCount: (totalCount: number) => void;
  setIsLoading: (isLoading: boolean) => void;
  /** Begins a loading cycle. Snapshots current rows into previousRows so the
   *  body can keep showing data instead of skeletons during refetch. */
  setIsLoadingWithSnapshot: () => void;
  /** Clears previousRows after new rows have been set. */
  clearPreviousRows: () => void;
  setQueryId: (queryId: string) => void;

  // Filter actions
  setFilterValues: (filterValues: Filters) => void;
  setStaticFilters: (staticFilters: QueryFilterObject) => void;
  /** Atomically reset filter values and show loading indicator. Preserves smart search. */
  resetFilterValues: () => void;

  // Selection actions
  setSelectedRowIds: (selectedRowIds: Set<string>) => void;
  toggleRowSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
  /** Atomic single-select: clears all selections and selects the given ID.
   *  If the ID is already the only selection, deselects it (toggle behavior). */
  setSingleSelection: (id: string) => void;

  // Settings actions
  setGridSettings: (settings: Partial<FeGridSettings>) => void;

  // UI actions
  setPage: (page: number) => void;
  setActivePanel: (activePanel: ToolpanelType | null) => void;
  setIsResizing: (isResizing: boolean) => void;
  setResizingColumn: (resizingColumn: string | Nullish) => void;
  startResizing: (colKey: string) => void;
  endResizing: () => void;

  // Drag actions
  setActiveDragId: (activeDragId: string | Nullish) => void;
  setDragOver: (overColumnId: string | Nullish, dropSide: 'left' | 'right' | Nullish) => void;
  clearDragState: () => void;
  setChoiceFilterOptions: (columnKey: string, options: ChoiceFilterOptions) => void;

  // Preset actions
  setActivePreset: (activePreset: ActivePreset | Nullish) => void;
  setCustomPresets: (customPresets: Preset[] | null) => void;
  setVirtualPreset: (virtualPreset: VirtualPreset | Nullish) => void;
  setIsPresetChanged: (isPresetChanged: boolean) => void;
  setActivePresetDataQuery: (activePresetDataQuery: GetDataQueryResponse | Nullish) => void;

  // Core actions
  setDataQuery: (dataQuery: GetDataQueryResponse | Nullish) => void;
  setDefinition: (definition: TransformedDefinitionApiResponse | Nullish) => void;
  setApiError: (apiError: string | Nullish) => void;

  // Action lock actions
  lockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => void;
  unlockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => void;
  isActionLocked: (rowId: string, actionKey: string) => boolean;
  clearPromiseLocks: () => void;
  clearLocksForRow: (rowId: string) => void;

  // Bulk action loading
  setIsBulkActionLoading: (loading: boolean) => void;

  // Static config — call once on mount, never again
  initStaticConfig: (config: {
    gridCode: string;
    presetInstanceId?: string;
    disableAllActions?: boolean;
  }) => void;
}

/**
 * Factory that creates a new, isolated EagGrid store for a single grid instance.
 *
 * Call once per `<EagGrid>` mount inside `EagGridStoreProvider`.  The returned
 * object is stored in a React ref so it survives re-renders without re-creating
 * the store.
 *
 * @param initialState - Optional partial override for the initial state (used in
 *   tests and for Storybook hydration scenarios).
 */
export function createEagGridStore(initialState?: Partial<EagGridState>): EagGridStore {
  const store = createStore<EagGridState>()(() => ({
    gridCode: '',
    presetInstanceId: null,
    disableAllActions: false,
    dataQuery: null,
    definition: null,
    apiError: null,
    activePreset: null,
    customPresets: null,
    // null = not yet loaded; [] = loaded with no presets
    virtualPreset: null,
    isPresetChanged: false,
    activePresetDataQuery: null,
    columnStates: [],
    // fresh array per factory call
    rows: [],
    previousRows: [],
    totalCount: 0,
    isLoading: true,
    // true until first fetch completes — prevents empty-state flash
    queryId: '',
    filterValues: {},
    staticFilters: {},
    selectedRowIds: new Set<string>(),
    // fresh Set per factory call
    page: 0,
    activePanel: null,
    isColumnsReady: false,
    isResizing: false,
    resizingColumn: null,
    activeDragId: null,
    overColumnId: null,
    dropSide: null,
    choiceFilterOptionsCache: new Map(),
    actionLocks: new Map(),
    isBulkActionLoading: false,
    ...initialState
  }));
  const actions = {
    // Column actions
    setColumnStates: (columnStates: EagColumnState[]) => {
      store.setState({
        columnStates
      });
    },
    setColumnWidth: (key: string, width: number) => {
      const state = store.getState();
      const target = state.columnStates.find(col => col.key === key);
      if (target && target.width === width) {
        return;
      }
      store.setState({
        columnStates: state.columnStates.map(col => col.key === key ? {
          ...col,
          width
        } : col)
      });
    },
    setColumnHidden: (key: string, hidden: boolean) => {
      const state = store.getState();
      const target = state.columnStates.find(col => col.key === key);
      if (target && (target.hidden ?? false) === hidden) {
        return;
      }
      store.setState({
        columnStates: state.columnStates.map(col => col.key === key ? {
          ...col,
          hidden
        } : col)
      });
    },
    setColumnPinned: (key: string, pinned: 'LEFT' | 'RIGHT' | null) => {
      store.setState({
        columnStates: store.getState().columnStates.map(col => col.key === key ? {
          ...col,
          pinned
        } : col)
      });
    },
    reorderColumnsByKey: (fromKey: string, toKey: string) => {
      const state = store.getState();
      const fromIndex = state.columnStates.findIndex(cs => cs.key === fromKey);
      const toIndex = state.columnStates.findIndex(cs => cs.key === toKey);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }
      const reordered = move(fromIndex, toIndex, state.columnStates);
      store.setState({
        columnStates: reordered.map((col, index) => ({
          ...col,
          order: index
        }))
      });
    },
    setColumnSort: (key: string, sort: 'ASC' | 'DESC' | null) => {
      const state = store.getState();
      const newColumnStates = state.columnStates.map(col => col.key === key ? {
        ...col,
        sort
      } : {
        ...col,
        sort: null
      });

      // Keep dataQuery.order in sync so applyPresetColumnsAndSort reads current sort
      // after preset updates (prevents stale sort from overwriting user changes).
      const updatedDataQuery = state.dataQuery ? {
        ...state.dataQuery,
        order: getSortModel(newColumnStates)
      } : state.dataQuery;
      store.setState({
        columnStates: newColumnStates,
        dataQuery: updatedDataQuery
      });
    },
    applyPresetColumns: (columns: ColumnsSetting[]) => {
      const state = store.getState();
      const presetMap = new Map(columns.map(column => [column.columnKey, column]));
      store.setState({
        isColumnsReady: true,
        columnStates: state.columnStates.map(col => match(presetMap.get(col.key)).with(undefined, () => col).otherwise(preset => ({
          ...col,
          hidden: !preset.visible,
          width: preset.width,
          pinned: preset.pinned
        })))
      });
    },
    applyPresetColumnsAndSort: (columns: ColumnsSetting[]) => {
      const state = store.getState();
      const order = state.dataQuery?.order;
      const presetMap = new Map(columns.map(column => [column.columnKey, column]));
      const sortMap = order ? new Map(order.map((o, i) => [o.columnKey, {
        sort: o.order,
        sortIndex: i
      }])) : null;
      store.setState({
        isColumnsReady: true,
        columnStates: state.columnStates.map(col => {
          const preset = presetMap.get(col.key);
          const sortEntry = sortMap?.get(col.key);
          const sortFields = sortEntry ? {
            sort: sortEntry.sort,
            sortIndex: sortEntry.sortIndex
          } : {
            sort: null as 'ASC' | 'DESC' | null,
            sortIndex: undefined
          };
          return preset ? {
            ...col,
            hidden: !preset.visible,
            width: preset.width,
            pinned: preset.pinned,
            ...sortFields
          } : {
            ...col,
            ...sortFields
          };
        })
      });
    },
    // Server data actions
    setRows: (rows: RowData[]) => {
      store.setState({
        rows
      });
    },
    setTotalCount: (totalCount: number) => {
      if (store.getState().totalCount === totalCount) {
        return;
      }
      store.setState({
        totalCount
      });
    },
    setIsLoading: (isLoading: boolean) => {
      if (store.getState().isLoading === isLoading) {
        return;
      }
      store.setState({
        isLoading
      });
    },
    setIsLoadingWithSnapshot: () => {
      store.setState({
        isLoading: true,
        previousRows: store.getState().rows
      });
    },
    clearPreviousRows: () => {
      store.setState({
        previousRows: []
      });
    },
    setQueryId: (queryId: string) => {
      store.setState({
        queryId
      });
    },
    // Filter actions
    setFilterValues: (filterValues: Filters) => {
      if (store.getState().filterValues === filterValues) {
        return;
      }
      store.setState({
        filterValues
      });
    },
    setStaticFilters: (staticFilters: QueryFilterObject) => {
      if (store.getState().staticFilters === staticFilters) {
        return;
      }
      store.setState({
        staticFilters
      });
    },
    resetFilterValues: () => {
      const state = store.getState();
      const current = state.filterValues;
      store.setState({
        isLoading: true,
        previousRows: state.rows,
        filterValues: SMART_SEARCH_KEY in current ? {
          [SMART_SEARCH_KEY]: current[SMART_SEARCH_KEY]
        } : {}
      });
    },
    // Settings actions
    setGridSettings: (settings: Partial<FeGridSettings>) => {
      const state = store.getState();
      if (!state.activePreset) {
        return;
      }
      store.setState({
        activePreset: {
          ...state.activePreset,
          gridSettings: {
            ...state.activePreset.gridSettings,
            ...settings
          }
        }
      });
    },
    // Selection actions
    setSelectedRowIds: (selectedRowIds: Set<string>) => {
      store.setState({
        selectedRowIds
      });
    },
    toggleRowSelection: (id: string) => {
      const next = new Set(store.getState().selectedRowIds);
      match(next.has(id)).with(true, () => next.delete(id)).with(false, () => next.add(id)).exhaustive();
      store.setState({
        selectedRowIds: next
      });
    },
    selectAll: (ids: string[]) => {
      store.setState({
        selectedRowIds: new Set(ids)
      });
    },
    deselectAll: () => {
      if (store.getState().selectedRowIds.size === 0) {
        return;
      }
      store.setState({
        selectedRowIds: new Set()
      });
    },
    setSingleSelection: (id: string) => {
      const current = store.getState().selectedRowIds;
      // Toggle off if already the only selected row
      if (current.size === 1 && current.has(id)) {
        store.setState({
          selectedRowIds: new Set()
        });
        return;
      }
      store.setState({
        selectedRowIds: new Set([id])
      });
    },
    // UI actions
    setPage: (page: number) => {
      if (store.getState().page === page) {
        return;
      }
      store.setState({
        page
      });
    },
    setActivePanel: (activePanel: ToolpanelType | null) => {
      store.setState({
        activePanel
      });
    },
    setIsResizing: (isResizing: boolean) => {
      if (store.getState().isResizing === isResizing) {
        return;
      }
      store.setState({
        isResizing
      });
    },
    setResizingColumn: (resizingColumn: string | Nullish) => {
      if (store.getState().resizingColumn === resizingColumn) {
        return;
      }
      store.setState({
        resizingColumn
      });
    },
    startResizing: (colKey: string) => {
      const state = store.getState();
      if (state.isResizing && state.resizingColumn === colKey) {
        return;
      }
      store.setState({
        isResizing: true,
        resizingColumn: colKey
      });
    },
    endResizing: () => {
      if (!store.getState().isResizing) {
        return;
      }
      store.setState({
        isResizing: false,
        resizingColumn: null
      });
    },
    // Drag actions
    setActiveDragId: (activeDragId: string | Nullish) => {
      if (store.getState().activeDragId === activeDragId) {
        return;
      }
      store.setState({
        activeDragId
      });
    },
    clearDragState: () => {
      const state = store.getState();
      if (state.activeDragId === null && state.overColumnId === null && state.dropSide === null) {
        return;
      }
      store.setState({
        activeDragId: null,
        overColumnId: null,
        dropSide: null
      });
    },
    setDragOver: (overColumnId: string | Nullish, dropSide: 'left' | 'right' | Nullish) => {
      const state = store.getState();
      if (state.overColumnId === overColumnId && state.dropSide === dropSide) {
        return;
      }
      store.setState({
        overColumnId,
        dropSide
      });
    },
    setChoiceFilterOptions: (columnKey: string, options: ChoiceFilterOptions) => {
      const newCache = new Map(store.getState().choiceFilterOptionsCache);
      newCache.set(columnKey, options);
      store.setState({
        choiceFilterOptionsCache: newCache
      });
    },
    // Preset actions
    setActivePreset: (activePreset: ActivePreset | Nullish) => {
      store.setState({
        activePreset
      });
    },
    setCustomPresets: (customPresets: Preset[] | null) => {
      store.setState({
        customPresets
      });
    },
    setVirtualPreset: (virtualPreset: VirtualPreset | Nullish) => {
      store.setState({
        virtualPreset
      });
    },
    setIsPresetChanged: (isPresetChanged: boolean) => {
      if (store.getState().isPresetChanged === isPresetChanged) {
        return;
      }
      store.setState({
        isPresetChanged
      });
    },
    setActivePresetDataQuery: (activePresetDataQuery: GetDataQueryResponse | Nullish) => {
      store.setState({
        activePresetDataQuery
      });
    },
    // Core actions
    setDataQuery: (dataQuery: GetDataQueryResponse | Nullish) => {
      store.setState({
        dataQuery
      });
    },
    setDefinition: (definition: TransformedDefinitionApiResponse | Nullish) => {
      store.setState({
        definition
      });
    },
    setApiError: (apiError: string | Nullish) => {
      store.setState({
        apiError
      });
    },
    // Action lock actions
    lockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => {
      const state = store.getState();
      const key = makeActionLockKey(rowId, actionKey, reason);
      if (state.actionLocks.has(key)) {
        return;
      }
      const next = new Map(state.actionLocks);
      next.set(key, Date.now());
      store.setState({
        actionLocks: next
      });
    },
    unlockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => {
      const state = store.getState();
      const key = makeActionLockKey(rowId, actionKey, reason);
      if (!state.actionLocks.has(key)) {
        return;
      }
      const next = new Map(state.actionLocks);
      next.delete(key);
      store.setState({
        actionLocks: next
      });
    },
    isActionLocked: (rowId: string, actionKey: string) => {
      const state = store.getState();
      return state.actionLocks.has(makeActionLockKey(rowId, actionKey, 'promise')) || state.actionLocks.has(makeActionLockKey(rowId, actionKey, 'manual'));
    },
    clearPromiseLocks: () => {
      const state = store.getState();
      let changed = false;
      const next = new Map(state.actionLocks);
      for (const key of next.keys()) {
        if (key.endsWith(':promise')) {
          next.delete(key);
          changed = true;
        }
      }
      if (!changed) {
        return;
      }
      store.setState({
        actionLocks: next
      });
    },
    clearLocksForRow: (rowId: string) => {
      const state = store.getState();
      const prefix = `${rowId}:`;
      let changed = false;
      const next = new Map(state.actionLocks);
      for (const key of next.keys()) {
        if (key.startsWith(prefix)) {
          next.delete(key);
          changed = true;
        }
      }
      if (!changed) {
        return;
      }
      store.setState({
        actionLocks: next
      });
    },
    setIsBulkActionLoading: (loading: boolean) => {
      if (store.getState().isBulkActionLoading === loading) {
        return;
      }
      store.setState({
        isBulkActionLoading: loading
      });
    },
    initStaticConfig: (config: {
      gridCode: string;
      presetInstanceId?: string;
      disableAllActions?: boolean;
    }) => {
      store.setState({
        gridCode: config.gridCode,
        presetInstanceId: config.presetInstanceId ?? null,
        disableAllActions: config.disableAllActions ?? false
      });
    }
  };
  return Object.assign(store, actions);
}

// Selector functions — each takes EagGridState and returns a derived value.
// Use with useEagGridSelector: useEagGridSelector(selectPage)
export const selectDataQuery = (state: EagGridState) => state.dataQuery;
export const selectDefinition = (state: EagGridState) => state.definition;
export const selectApiError = (state: EagGridState) => state.apiError;
export const selectActivePreset = (state: EagGridState) => state.activePreset;
export const selectCustomPresets = (state: EagGridState) => state.customPresets;
export const selectVirtualPreset = (state: EagGridState) => state.virtualPreset;
export const selectIsPresetChanged = (state: EagGridState) => state.isPresetChanged;
export const selectActivePresetDataQuery = (state: EagGridState) => state.activePresetDataQuery;
export const selectColumnStates = (state: EagGridState) => state.columnStates;
export const selectRows = (state: EagGridState) => state.rows;
export const selectTotalCount = (state: EagGridState) => state.totalCount;
export const selectIsLoading = (state: EagGridState) => state.isLoading;
export const selectQueryId = (state: EagGridState) => state.queryId;
export const selectFilterValues = (state: EagGridState) => state.filterValues;
export const selectSelectedRowIds = (state: EagGridState) => state.selectedRowIds;
export const selectPage = (state: EagGridState) => state.page;
export const selectActivePanel = (state: EagGridState) => state.activePanel;
export const selectIsResizing = (state: EagGridState) => state.isResizing;
export const selectResizingColumn = (state: EagGridState) => state.resizingColumn;
export const selectPreviousRows = (state: EagGridState) => state.previousRows;
export const selectGridSettings = (state: EagGridState) => state.activePreset?.gridSettings ?? null;
export const selectHorizontalSeparatorsVisible = (state: EagGridState) => state.activePreset?.gridSettings?.horizontalSeparatorsVisible ?? true;
export const selectVerticalSeparatorsVisible = (state: EagGridState) => state.activePreset?.gridSettings?.verticalSeparatorsVisible ?? true;
export const selectColumnGroupsVisible = (state: EagGridState) => state.activePreset?.gridSettings?.columnGroupsVisible ?? false;
export const selectActiveDragId = (state: EagGridState) => state.activeDragId;
export const selectIsColumnsReady = (state: EagGridState) => state.isColumnsReady;
export const selectIsBulkActionLoading = (state: EagGridState) => state.isBulkActionLoading;

/** Filter values excluding any static filters injected by queryModifier. */
export const selectFilterValuesWithoutStaticFilters = (state: EagGridState): Filters => omit(keys(state.staticFilters) as string[], state.filterValues) as Filters;

// Static config selectors
export const selectGridCode = (state: EagGridState): string => state.gridCode;

// Per-column selector factory with stable function references via Map cache.
const choiceFilterOptionsSelectorCache = new Map<string, (state: EagGridState) => ChoiceFilterOptions | undefined>();
export function selectChoiceFilterOptions(columnKey: string): (state: EagGridState) => ChoiceFilterOptions | undefined {
  let selector = choiceFilterOptionsSelectorCache.get(columnKey);
  if (!selector) {
    selector = (state: EagGridState) => state.choiceFilterOptionsCache.get(columnKey);
    choiceFilterOptionsSelectorCache.set(columnKey, selector);
  }
  return selector;
}
export const selectActionLocks = (state: EagGridState) => state.actionLocks;